import React from 'react';

import { Modal } from 'baukasten';

import { useTranslationOverrides } from 'utils/hooks/useTranslationOverrides';
import { useCurrentAccount } from 'utils/hooks/globalState/baseState/useCurrentAccount';
import { assertUnreachable } from 'types/utilityTypes';

import { LicenseType } from './types';
import { LicenseUpgradeModalBody } from './components/LicenseUpgradeModalBody';
import { LicenseUpgradeModalFooter } from './components/LicenseUpgradeModalFooter';
import messages from './messages';
import { useCanSelfUpgradeViewerLicense } from './hooks/useCanSelfUpgradeViewerLicense';
import { useUpgradeToStandardUser } from './hooks/useUpgradeToStandardUser';

type LicenseUpgradeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  upgradeToLicense: LicenseType;
};

export const LicenseUpgradeModal = ({
  isOpen,
  onClose,
  upgradeToLicense,
}: LicenseUpgradeModalProps) => {
  const { formatMessage } = useTranslationOverrides();

  const account = useCurrentAccount();

  const canSelfUpgradeViewerLicense = useCanSelfUpgradeViewerLicense();
  const { upgradeToStandardUser, isLoading, isSuccess } =
    useUpgradeToStandardUser();

  const handleClose = () => {
    if (isSuccess) {
      window.location.reload();
    } else {
      onClose();
    }
  };

  if (!account) {
    return null;
  }

  let modalTitle: string;

  switch (upgradeToLicense) {
    case 'standard':
      modalTitle = formatMessage(messages.upgradeFromViewerToStandardTitle);
      break;
    default:
      assertUnreachable(upgradeToLicense);
  }

  return (
    <Modal
      isModalOpen={isOpen}
      onCloseModal={handleClose}
      title={modalTitle}
      ariaLabel="License upgrade modal"
      footer={
        <LicenseUpgradeModalFooter
          onClose={handleClose}
          account={account}
          canSelfUpgradeViewerLicense={canSelfUpgradeViewerLicense}
          didUpgradeSuccessfully={isSuccess}
          isLoading={isLoading}
          onUpgradeClick={upgradeToStandardUser}
        />
      }
      maxWidth="800px"
      pl={6}
      pr={6}
      pt={6}
      pb={6}
    >
      <LicenseUpgradeModalBody
        account={account}
        upgradeToLicense={upgradeToLicense}
        canSelfUpgradeViewerLicense={canSelfUpgradeViewerLicense}
        didUpgradeSuccessfully={isSuccess}
      />
    </Modal>
  );
};
